import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Newspaper, Users, Target, TrendingUp, CheckCircle2 } from 'lucide-react';
import SEO from '@/components/SEO';

const MediaDistribution = () => {
  const distributionChannels = [
    {
      icon: Newspaper,
      title: 'News Wire Services',
      description: 'Your press release distributed through major wire services including PR Newswire, Business Wire, and exclusive Ixoraly network reaching thousands of journalists.'
    },
    {
      icon: Globe,
      title: 'Online Media Outlets',
      description: 'Direct distribution to 500+ online news websites, industry publications, and digital media platforms across multiple sectors and geographies.'
    },
    {
      icon: Users,
      title: 'Journalist Database',
      description: 'Targeted email distribution to our curated database of 10,000+ journalists, editors, and media professionals organized by beat and industry.'
    },
    {
      icon: Target,
      title: 'Social Media Amplification',
      description: 'Automated sharing across major social media platforms to extend reach and drive engagement with your target audience.'
    },
    {
      icon: TrendingUp,
      title: 'Google News & Search',
      description: 'SEO-optimized distribution ensuring your press release appears in Google News and achieves high search engine rankings.'
    }
  ];

  const features = [
    'Distribution to 500+ media outlets worldwide',
    'Targeted journalist email alerts',
    'Google News approved distribution',
    'Social media syndication',
    'Permanent online archive',
    'Multimedia content support',
    'Geographic and industry targeting',
    'Real-time distribution tracking'
  ];

  const industries = [
    'Technology & Software',
    'Healthcare & Pharmaceuticals',
    'Financial Services & Banking',
    'Manufacturing & Industrial',
    'Consumer Goods & Retail',
    'Energy & Environment',
    'Education & E-Learning',
    'Entertainment & Media',
    'Real Estate & Construction',
    'Professional Services'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Media Distribution Network"
        description="Distribute press releases to 500+ media outlets, news sites, and journalist networks. Ixoraly's media distribution ensures maximum coverage for your news."
        path="/media-distribution"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="media-distribution-heading">
              Professional Media Distribution
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Reach journalists, media outlets, and audiences worldwide with our comprehensive press release distribution network.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-colors"
            >
              View Distribution Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Distribution Channels */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Distribution Channels
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Multi-channel distribution strategy ensuring maximum visibility and reach for your press releases
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {distributionChannels.map((channel, index) => {
              const Icon = channel.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-white rounded-xl border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-navy-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{channel.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{channel.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Distribution Features
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our media distribution service includes everything you need to maximize your press release impact and ensure your news reaches the right audience.
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0" size={24} />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Distribution Stats</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Media Outlets</span>
                  <span className="text-3xl font-bold text-navy-600">500+</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Journalists Reached</span>
                  <span className="text-3xl font-bold text-navy-600">10K+</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-700 font-medium">Countries Covered</span>
                  <span className="text-3xl font-bold text-navy-600">50+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Average Pickup Rate</span>
                  <span className="text-3xl font-bold text-green-600">85%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Industries We Cover
            </h2>
            <p className="text-lg text-gray-600">
              Specialized distribution networks for every major industry sector
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="p-4 bg-navy-50 rounded-lg text-center hover:bg-navy-100 transition-colors"
              >
                <p className="text-sm font-medium text-gray-800">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Distribute Your Press Release?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Choose from our flexible distribution packages and get your news in front of the media that matters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-press-release"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-colors"
            >
              Submit Press Release
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20"
            >
              Contact Sales Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaDistribution;
